// src/components/draft/NotificationSettings.js - Notification Settings Panel
import React, { useState, useEffect } from 'react';
import { Bell, BellOff, Volume2, VolumeX, Settings } from 'lucide-react';
import { dynastyTheme } from '../../services/colorService';
import notificationService from '../../services/notificationService';

const NotificationSettings = () => {
  const [soundsEnabled, setSoundsEnabled] = useState(notificationService.getSoundsEnabled());
  const [notificationsEnabled, setNotificationsEnabled] = useState(notificationService.getNotificationsEnabled());
  const [permissionStatus, setPermissionStatus] = useState(notificationService.getPermissionStatus());
  
  // Update state when service changes
  useEffect(() => {
    setSoundsEnabled(notificationService.getSoundsEnabled());
    setNotificationsEnabled(notificationService.getNotificationsEnabled());
    setPermissionStatus(notificationService.getPermissionStatus());
  }, []);
  
  const handleToggleSounds = () => {
    const newValue = !soundsEnabled;
    setSoundsEnabled(newValue);
    notificationService.setSoundsEnabled(newValue);
    
    // Test sound if enabling
    if (newValue) {
      notificationService.playSound('pick-made');
    }
  };
  
  const handleToggleNotifications = () => {
    const newValue = !notificationsEnabled;
    setNotificationsEnabled(newValue);
    notificationService.setNotificationsEnabled(newValue);
  };
  
  const handleRequestPermission = async () => {
    const granted = await notificationService.requestPermission();
    setPermissionStatus(notificationService.getPermissionStatus());
    
    if (granted) {
      // Test notification
      notificationService.showBrowserNotification(
        'Notifications Enabled',
        'You will now receive draft notifications',
        { tag: 'test' }
      );
    }
  };
  
  return (
    <div className={`${dynastyTheme.components.card.base} p-4`}>
      <div className="flex items-center gap-2 mb-4">
        <Settings className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
        <h3 className={dynastyTheme.components.heading.h3}>
          Notification Settings
        </h3>
      </div>
      
      <div className="space-y-3">
        {/* Sounds Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {soundsEnabled ? (
              <Volume2 className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
            ) : (
              <VolumeX className={`w-5 h-5 ${dynastyTheme.classes.text.neutralDark}`} />
            )}
            <span className={dynastyTheme.classes.text.white}>Sound Effects</span>
          </div>
          <button
            onClick={handleToggleSounds}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              soundsEnabled ? dynastyTheme.classes.bg.primary : 'bg-gray-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                soundsEnabled ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        
        {/* Notifications Toggle */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {notificationsEnabled ? (
              <Bell className={`w-5 h-5 ${dynastyTheme.classes.text.primary}`} />
            ) : (
              <BellOff className={`w-5 h-5 ${dynastyTheme.classes.text.neutralDark}`} />
            )}
            <span className={dynastyTheme.classes.text.white}>Browser Notifications</span>
          </div>
          <button
            onClick={handleToggleNotifications}
            disabled={permissionStatus === 'denied'}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              notificationsEnabled && permissionStatus === 'granted'
                ? dynastyTheme.classes.bg.primary
                : 'bg-gray-600'
            } ${permissionStatus === 'denied' ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                notificationsEnabled && permissionStatus === 'granted' ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
        
        {/* Permission Status */}
        {permissionStatus === 'default' && (
          <button
            onClick={handleRequestPermission}
            className={`w-full ${dynastyTheme.components.button.secondary} text-sm`}
          >
            Enable Browser Notifications
          </button>
        )}
        
        {permissionStatus === 'denied' && (
          <p className={`text-xs ${dynastyTheme.classes.text.warning}`}>
            Notifications are blocked. Please enable them in your browser settings.
          </p>
        )}
        
        {permissionStatus === 'granted' && !notificationsEnabled && (
          <p className={`text-xs ${dynastyTheme.classes.text.neutralLight}`}>
            Notifications are enabled but turned off. Toggle above to enable.
          </p>
        )}
      </div>
    </div>
  );
};

export default NotificationSettings;

